import { Component, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Card } from '../ui';

/**
 * What the console shows when something inside it throws while rendering, instead of nothing.
 *
 * Without it React unmounts the whole tree and leaves a white page with no way back but
 * guessing F5. The case that found it: a lazily loaded map whose file is missing even after
 * the reload `main.tsx` does for a deploy, which `reloadForMissingChunk` refuses to repeat —
 * rightly, since a second reload would loop — and the refused error then emptied the page.
 * Any other render error lands here too.
 *
 * A class, because React still offers no hook for catching a render error.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  // No componentDidCatch: React 19 already reports a caught error to the console, with its
  // component stack, and a second copy would only be noise.
  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? <Stopped /> : this.props.children;
  }
}

function Stopped() {
  const { t } = useTranslation();

  return (
    <main className="min-h-screen grid place-items-center p-6">
      <Card className="w-[min(420px,100%)]">
        <div role="alert" className="flex flex-col gap-3.5 p-6">
          <p className="text-ink">{t('app.stopped')}</p>
          <div className="flex justify-end">
            <Button variant="primary" onClick={() => window.location.reload()}>
              {t('app.reload')}
            </Button>
          </div>
        </div>
      </Card>
    </main>
  );
}
